import React, { useCallback, useEffect, useState } from 'react';
import { fetchData } from 'services/api/fetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import Timeline from 'components/timeline/Timeline';
import JobDetailList from '../../views/jobs/JobDetailList';
import ContractJobScheduleView from './ContractJobScheduleView';
import { FormType } from '../../views/forms/FormTypes';
import { ContractJobScheduleType, ContractWorkflowOptionType } from './ContractTypes';

const ContractJobSchedulesTab: React.FC<{
    itemId: string;
    data?: any;
}> = ({
    itemId,
    data,
}) => {
    const { handleLogout, activeFeatures } = useAuthContext();
    const [schedules, setSchedules] = useState<ContractJobScheduleType[]>([]);
    const [forms, setForms] = useState<FormType[]>([]);
    const [workflows, setWorkflows] = useState<ContractWorkflowOptionType[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [hasJobs, setHasJobs] = useState(false);
    const [jobListKey, setJobListKey] = useState(0);

    // Fetch the schedules of the contract
    const fetchSchedules = useCallback(async () => {
        const response = await fetchData({
            apiUrl: 'get_contract_job_schedule_list',
            params: { contract: itemId, deleted: false },
            handleLogout
        });
        setSchedules(response.results || []);
        setLoaded(true);
    }, [itemId]);

    // Determine whether the contract has jobs to show the job list for
    const fetchHasJobs = useCallback(async () => {
        const response = await fetchData({
            apiUrl: 'get_job_list',
            params: { contract: itemId, deleted: false, page_size: 1 },
            handleLogout
        });
        setHasJobs((response.count || 0) > 0);
    }, [itemId]);

    // Load the schedules and the job count when opening the tab
    useEffect(() => {
        fetchSchedules();
        fetchHasJobs();
    }, [fetchSchedules, fetchHasJobs]);

    // Load the forms for the task rows when the forms module is active
    useEffect(() => {
        if (!activeFeatures.includes('forms_base')) return;
        const loadForms = async () => {
            const response = await fetchData({
                apiUrl: 'get_form_list',
                params: { deleted: false },
                handleLogout
            });
            setForms(response.results || []);
        };
        loadForms();
    }, []);

    // Load the workflows for the type dropdown when the job types module is active
    useEffect(() => {
        if (!activeFeatures.includes('job_types')) return;
        const loadWorkflows = async () => {
            const response = await fetchData({
                apiUrl: 'get_workflow_list',
                params: { is_active: true, connected_feature: 'job' },
                handleLogout
            });
            setWorkflows(response.results || []);
        };
        loadWorkflows();
    }, []);

    // Refresh the schedules and the job list after saving the schedule
    const handleSaved = () => {
        fetchSchedules();
        fetchHasJobs();
        setJobListKey(previousKey => previousKey + 1);
    };

    // A contract holds one schedule of recurring work
    const schedule = schedules[0];

    return (
        <div className='contract-job-schedules'>
            {loaded && (
                <ContractJobScheduleView
                    key={schedule ? `${schedule.id}-${schedule.modified}` : 'new'}
                    schedule={schedule}
                    contractId={parseInt(itemId)}
                    contractStartDate={data?.start_date}
                    contract={data}
                    forms={forms}
                    workflows={workflows}
                    onSaved={handleSaved}
                />
            )}
            {hasJobs && (
                <JobDetailList
                    key={jobListKey}
                    itemId={itemId}
                    detailPage='contract'
                    data={data}
                />
            )}
            <div className='contract-timeline'>
                <Timeline
                    placement='page'
                    timeline_page='contract'
                    timeline_page_id={parseInt(itemId)}
                    noteOnly={true}
                    data={data}
                />
            </div>
        </div>
    );
};

export default ContractJobSchedulesTab;
